/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import i18n from 'i18n';
import { connect } from 'react-redux';
import {
  getDashboardPartnerRequest,
  getPartnerDataGraphRequest,
} from 'modules/dashboard/actions';
import ApplicationState from 'store/interfaces';
import { DashboardDataCompanyPartnerFields } from 'modules/dashboard/interfaces';
import Permission from 'components/ValidPermission';
import TodayIcon from '@material-ui/icons/Today';
import CloseIcon from '@material-ui/icons/Close';
import { Grid, IconButton, Typography, makeStyles } from '@material-ui/core';
import { CompanyData, PartnerData } from 'modules/authenticate/interfaces';
import DialogList from 'components/DialogList';
import Alert from '@material-ui/lab/Alert';
import DatePicker from 'components/DatePicker';
import { Formik } from 'formik';
import { formatDateForDateFilter } from 'utils/tsHelpers';
import Button from 'components/Button';
import clsx from 'clsx';
import MainPanelTemplate from '../templates';
import styles from './styles';
import SessionsHistoryPartnerCard from './SessionsHistoryPartnerCard';
import InstancesHistoryPartnerCard from './InstancesHistoryPartnerCard';
import PartnerDashboardCard from './PartnerDashboardCard';
import ClientsHistory from './ClientsHistory';
import EnvironmentsCostPartnerCard from './EnvironmentsCostPartnerCard';

interface DatesProps {
  start_date: string;
  end_date: string;
}

interface Props {
  companySelected: CompanyData | null;
  companyDashboardPartnerData: DashboardDataCompanyPartnerFields;
  intervalTime: any;
  isLoading: boolean;
  partnerSelected: PartnerData | null;
  getDashboardPartnerRequest: (props) => void;
  getPartnerDataGraphRequest: ({
    companyUuid,
    partnerUuid,
    intervalTime,
    start_date,
    end_date,
  }) => void;
}

const ClientsPanel = ({
  companySelected,
  companyDashboardPartnerData,
  intervalTime,
  isLoading,
  partnerSelected,
  getDashboardPartnerRequest,
  getPartnerDataGraphRequest,
}: Props) => {
  const useStyles = makeStyles(styles);
  const classes = useStyles({});
  const [dialogListOpened, setDialogOpened] = React.useState<
    'company' | 'partner' | null
  >(null);
  const [dateFilter, setDateFilter] = React.useState(true);

  const openDialogList = (type: 'company' | 'partner') => setDialogOpened(type);
  const [dates, setDates] = React.useState<DatesProps>({
    start_date: '',
    end_date: '',
  });

  React.useEffect(() => {
    if (companySelected && partnerSelected) {
      getDashboardPartnerRequest({
        companyUuid: companySelected.id,
        partnerUuid: partnerSelected.id,
      });
    }
  }, [companySelected, getDashboardPartnerRequest, partnerSelected]);

  React.useEffect(() => {
    if (companySelected && partnerSelected) {
      getPartnerDataGraphRequest({
        companyUuid: companySelected.id,
        partnerUuid: partnerSelected.id,
        intervalTime,
        start_date: dates?.start_date,
        end_date: dates?.end_date,
      });
    }
  }, [
    companySelected,
    getPartnerDataGraphRequest,
    intervalTime,
    partnerSelected,
    dates,
  ]);

  const handleSubmit = (values: DatesProps) => {
    setDates({
      start_date: formatDateForDateFilter(values?.start_date),
      end_date: formatDateForDateFilter(values?.end_date),
    });
  };

  return (
    <>
      {!partnerSelected && (
        <Alert className={classes.alert} severity="warning" color="info">
          <Grid item>
            <Typography>{i18n.t('SELECT_PARTNER_DASHBOARD')}</Typography>
          </Grid>
        </Alert>
      )}
      <MainPanelTemplate
        headerPage={{
          title: `${i18n.t('PARTNER_KEY')}`,
          selector: (
            <Button
              className={classes.buttonSelectPartner}
              variant="outlined"
              onClick={() => {
                openDialogList('partner');
              }}
            >
              <Typography className={classes.partnerSelector}>
                {companyDashboardPartnerData?.name ||
                  i18n.t('CHOOSE_PARTNER_KEY')}
              </Typography>
            </Button>
          ),
        }}
        cardLoading={isLoading}
      />
      {partnerSelected && (
        <>
          <Grid container spacing={2}>
            <Grid item className={classes.partnerDashboardCardContainer}>
              <Grid container className={classes.partnerDashboardCard}>
                <PartnerDashboardCard
                  companyDashboardPartnerData={companyDashboardPartnerData}
                />
              </Grid>
            </Grid>
            <Grid item className={classes.partnerDashboardCardContainer}>
              <Grid container className={classes.partnerDashboardCard}>
                <ClientsHistory />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            className={clsx(classes.noDateFilters, {
              [classes.dateFilters]: !dateFilter,
            })}
          >
            <Grid container justify="center">
              <Grid item className={classes.filterSelector}>
                {dateFilter ? (
                  <Button
                    startIcon={<TodayIcon />}
                    variant="outlined"
                    onClick={() => setDateFilter(false)}
                    className={classes.applyButton}
                  >
                    {i18n.t('APPLY_DATE_FILTERS_KEY')}
                  </Button>
                ) : (
                  <>
                    <Formik initialValues={dates} onSubmit={handleSubmit}>
                      {({ setFieldValue, submitForm, values }) => (
                        <Grid spacing={2} container justify="flex-start">
                          <IconButton
                            onClick={() => {
                              setDateFilter(true);
                              setDates({ start_date: '', end_date: '' });
                              setFieldValue('start_date', null);
                              setFieldValue('end_date', null);
                            }}
                          >
                            <CloseIcon className={classes.closeIcon} />
                          </IconButton>
                          <DatePicker
                            id="start_date"
                            type="datetimeFilter"
                            label={i18n.t('START_TIME_KEY')}
                            keyValue="start_date"
                            value={values?.start_date}
                            setFieldsValue={setFieldValue}
                            shrink={
                              values?.start_date !== '' &&
                              values?.start_date !== null &&
                              values?.start_date !== undefined
                            }
                          />
                          <DatePicker
                            id="end_date"
                            type="datetimeFilter"
                            label={i18n.t('END_TIME_KEY')}
                            keyValue="end_date"
                            value={values?.end_date}
                            minDate={values?.start_date}
                            setFieldsValue={setFieldValue}
                            shrink={
                              values?.end_date !== '' &&
                              values?.end_date !== null &&
                              values?.end_date !== undefined
                            }
                          />
                          <Grid item>
                            <Button
                              id="submit-button"
                              onClick={submitForm}
                              submitButton
                              disabled={
                                !values?.start_date ||
                                !values?.end_date ||
                                values?.start_date > values?.end_date
                              }
                            >
                              {i18n.t('APPLY_KEY')}
                            </Button>
                            {(values?.start_date || values?.end_date) && (
                              <Button
                                id="clear-button"
                                onClick={() => {
                                  setFieldValue('start_date', null);
                                  setFieldValue('end_date', null);
                                }}
                                deleteButton
                              >
                                {i18n.t('CLEAR_FORM_KEY')}
                              </Button>
                            )}
                          </Grid>
                        </Grid>
                      )}
                    </Formik>
                  </>
                )}
              </Grid>
            </Grid>
            <SessionsHistoryPartnerCard dateFilter={dateFilter} />
            <InstancesHistoryPartnerCard dateFilter={dateFilter} />
          </Grid>
          <Permission value="list_environment_cost">
            <Grid container alignItems="center" justify="center">
              <EnvironmentsCostPartnerCard />
            </Grid>
          </Permission>
        </>
      )}
      <DialogList
        open={dialogListOpened}
        setListOpened={value => setDialogOpened(value)}
        onClose={() => setDialogOpened(null)}
      />
    </>
  );
};
const mapStateToProps = ({
  dashboard: {
    companyDashboardData,
    companyDashboardPartnerData,
    isLoading,
    periodFilterPartnerGraph,
  },
  authenticate: { companySelected, partnerSelected },
}: ApplicationState) => ({
  companyDashboardData,
  companySelected,
  partnerSelected,
  isLoading,
  companyDashboardPartnerData,
  intervalTime: periodFilterPartnerGraph,
});

export default connect(mapStateToProps, {
  getDashboardPartnerRequest,
  getPartnerDataGraphRequest,
})(ClientsPanel);
